import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { useEffect, useState } from "react";
import clsx from "clsx";
import { getCategoryLable, getStatusLable, getPriorityLable, getTicketSeenState, TicketApiModel } from "./common";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE_URL = 'https://api.neontradepro.com';

const statusOrder: Record<TicketApiModel["status"], number> = {
    pending_admin: 0,
    in_progress: 1,
    pending_user: 2,
    closed: 3,
};

export default function TicketsList({ isAdmin = false }: { isAdmin?: boolean } = {}){
    const navigate = useNavigate();
    const location = useLocation();
    
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [tickets, setTickets] = useState<TicketApiModel[]>([]);
    const [error, setError] = useState<string>('');
    
    async function getTickets(){
        const token = localStorage.getItem('token');
        if (!token) return;
        setIsLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/tickets`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'خطا در دریافت تیکت‌ها');
            }

            const data = await response.json();
            data.sort((a: TicketApiModel, b: TicketApiModel) => {
                const statusDiff = statusOrder[a.status] - statusOrder[b.status];
                if (statusDiff !== 0) return statusDiff;
                return b.updated_at - a.updated_at;
            });
            setTickets(data);
        } catch (err: any) {
            console.error('Failed to fetch tickets:', err);
            setError(err.message);
        }
        finally{
            setIsLoading(false);
        }
    }

    function ticketOnClick(ticket: TicketApiModel){
        const queryParams = new URLSearchParams(location.search);
        const basePath = isAdmin ? `/admin/tickets/${ticket.id}` : `/dashboard/tickets/${ticket.id}`;
        navigate(`${basePath}?${queryParams.toString()}`);
    }

    useEffect(()=>{
        getTickets();
    }, [])

    if (error) return <Alert severity="error">{error}</Alert>;
    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;

    return <div className="ticket-page">
        <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">لیست تیکت‌ها</h2>
            {!isAdmin && <button
                className="ticket-send-button px-4 py-2 rounded transition-colors"
                onClick={() => navigate('/dashboard/tickets/new')}
            >
                تیکت جدید
            </button>}
        </div>
        <div className="ticket-table-shell relative overflow-x-auto sm:rounded-lg">
            <table className="ticket-table w-full text-sm text-left rtl:text-right">
                <thead>
                    <tr>
                        <th scope="col" className="px-6 py-3">
                            شناسه
                        </th>
                        <th scope="col" className="py-3">
                            عنوان
                        </th>
                        <th scope="col" className="py-3">
                            نام و نام خانوادگی
                        </th>
                        <th scope="col" className="py-3">
                            دسته‌بندی
                        </th>
                        <th scope="col" className="py-3">
                            وضعیت
                        </th>
                        <th scope="col" className="py-3">
                            اولویت
                        </th>
                    </tr>
                </thead>
                <tbody>
                {tickets.map((ticket, index)=>(
                    <tr className={clsx("ticket-table-row cursor-pointer", getTicketSeenState(ticket) === "new" && "ticket-row-new")} key={index} onClick={() => ticketOnClick(ticket)}>
                        <th scope="row" className="px-6 py-4 font-medium whitespace-nowrap">
                            #{ticket.id}
                        </th>
                        <td className="py-4">
                            {ticket.subject}
                        </td>
                        <td className="py-4">
                            {`${ticket.user.first_name || '-'} ${ticket.user.last_name || ''}`.trim()}
                            {ticket.user.username && <div className="ticket-secondary-text">@{ticket.user.username}</div>}
                        </td>
                        <td className="py-4">
                            {getCategoryLable(ticket.category)}
                        </td>
                        <td className="py-4">
                            {getStatusLable(ticket.status)}
                        </td>
                        <td className="py-4">
                            {getPriorityLable(ticket.priority)}
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    </div>
}
